import { useMemo } from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faTruck } from '@fortawesome/free-solid-svg-icons';
import { getWeekStartMoment } from '@s20/common/util/date';
import { useCalendarWeekStartsOn } from '@s20/hooks/tenant.hooks';

// Custom overlay header that sits on top of the Week View
const WeekViewHeaderOverlay = ({ events, currentDate }) => {
    const weekStartsOn = useCalendarWeekStartsOn();

    // Memoize the days calculation to prevent re-rendering on every array push
    const days = useMemo(() => {
        // Get the current week's date range
        const weekStart = getWeekStartMoment(currentDate, weekStartsOn);
        const daysArray = [];

        // Create data for each day of the week
        for (let day = 0; day < 7; day += 1) {
            const currentDay = moment(weekStart).add(day, 'days');

            // Filter events for this specific day (excluding header events)
            const dayEvents = events.filter((event) =>
                !event.resource?.isHeader &&
                moment(event.start).isSame(currentDay, 'day'),
            );

            // Count jobs and resources
            let jobCount = 0;
            let totalStaff = 0;
            let totalVehicles = 0;

            dayEvents.forEach((event) => {
                if (event.resource?.isGrouped) {
                    // Grouped event
                    jobCount += event.resource.jobCount || event.resource.groupedEvents?.length || 0;
                    totalStaff += event.resource.totalStaff || 0;
                    totalVehicles += event.resource.totalVehicles || 0;
                } else if (event.resource) {
                    // Single job
                    jobCount += 1;
                    const staff = event.resource.staffCount || event.resource.StaffCount || 0;
                    const vehicles = event.resource.vehicleCount || event.resource.VehicleCount || 0;
                    totalStaff += staff;
                    totalVehicles += vehicles;
                }
            });

            daysArray.push({
                date: currentDay,
                jobCount,
                totalStaff,
                totalVehicles,
            });
        }

        return daysArray;
    }, [events, currentDate, weekStartsOn]);

    return (
        <div className="week-header-overlay">
            {days.map((day, index) => (
                <div
                    key={index}
                    className="week-header-day"
                >
                    <div className="header-content">
                        <div className="jobs-row">
                            <span className="job-count">
                                {day.jobCount} {day.jobCount === 1 ? 'job' : 'jobs'}
                            </span>
                        </div>
                        {(day.totalStaff > 0 || day.totalVehicles > 0) && (
                            <div className="resources-row">
                                {day.totalStaff > 0 && (
                                    <span className="resource-count staff">
                                        <FontAwesomeIcon icon={faUsers} />
                                        {day.totalStaff}
                                    </span>
                                )}
                                {day.totalVehicles > 0 && (
                                    <span className="resource-count vehicles">
                                        <FontAwesomeIcon icon={faTruck} />
                                        {day.totalVehicles}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default WeekViewHeaderOverlay;